import type Phaser from "phaser";
import { CANVAS_COLOURS, type CanvasColour } from "daedalus-core";

/**
 * The areas a selected thing's cells fields hold (ADR-070's amendment "the other field kinds"), drawn in
 * `kind-zones`: each cell filled at `FIELD_CELLS_FILL.shown`, the one being painted at `FIELD_CELLS_FILL.painting`,
 * with a 1 px edge round each area at any zoom. One `Graphics` object over the map, redrawn only when the areas
 * or the zoom change.
 */

/**
 * The fill's opacity for an area shown and for the one being painted.
 */
export const FIELD_CELLS_FILL = { shown: 0.3, painting: 0.45 } as const;

/**
 * The edge's width in screen pixels.
 */
export const FIELD_CELLS_EDGE = 1;

/**
 * One area to draw: its thing's anchor in world pixels, a cell's size in pixels, its `[column, rowsUp]` cells
 * and whether it is the one being painted. A `colour` of its own draws it in that colour in place of the field's.
 */
export interface FieldCellsArea {
  readonly anchor: { readonly x: number; readonly y: number };
  readonly cellPixels: number;
  readonly cells: readonly (readonly [number, number])[];
  readonly painting: boolean;
  readonly colour?: CanvasColour;
}

/**
 * The edge segments round an area, in world pixels: every side of a cell whose neighbour across it is not in
 * the area.
 *
 * @param area The area.
 * @returns The segments as `[x0, y0, x1, y1]`.
 */
export function areaEdges(area: FieldCellsArea): [number, number, number, number][] {
  const keys = new Set(area.cells.map(([c, r]) => `${c},${r}`));
  const { anchor, cellPixels: size } = area;
  const edges: [number, number, number, number][] = [];
  for (const [c, r] of area.cells) {
    const left = anchor.x + c * size;
    const top = anchor.y - (r + 1) * size;
    if (!keys.has(`${c - 1},${r}`)) {
      edges.push([left, top, left, top + size]);
    }
    if (!keys.has(`${c + 1},${r}`)) {
      edges.push([left + size, top, left + size, top + size]);
    }
    if (!keys.has(`${c},${r + 1}`)) {
      edges.push([left, top, left + size, top]);
    }
    if (!keys.has(`${c},${r - 1}`)) {
      edges.push([left, top + size, left + size, top + size]);
    }
  }
  return edges;
}

/**
 * The areas of one scene.
 */
export class FieldCellsView {
  private readonly graphics: Phaser.GameObjects.Graphics;
  private last = "";
  private lastAreas: readonly FieldCellsArea[] | null = null;
  private lastZoom = Number.NaN;

  /**
   * @param scene Scene to draw into.
   * @param depth Depth over the map's layers.
   */
  public constructor(scene: Phaser.Scene, depth: number) {
    this.graphics = scene.add.graphics().setDepth(depth);
  }

  /**
   * Draws the areas, or clears them, when they or the zoom changed since the last call. The same array at the same zoom
   * is the same drawing, so a frame that changed nothing builds nothing.
   *
   * @param areas The areas.
   * @param zoom The camera's zoom.
   * @returns True when a redraw happened.
   */
  public sync(areas: readonly FieldCellsArea[], zoom: number): boolean {
    if (areas === this.lastAreas && zoom === this.lastZoom) {
      return false;
    }
    this.lastAreas = areas;
    this.lastZoom = zoom;
    const signature = `${zoom}|${areas.map((a) => `${a.anchor.x},${a.anchor.y},${a.cellPixels},${a.painting ? 1 : 0},${a.colour?.rgb ?? ""}:${a.cells.map(([c, r]) => `${c},${r}`).join(" ")}`).join(";")}`;
    if (signature === this.last) {
      return false;
    }
    this.last = signature;
    const g = this.graphics;
    g.clear();
    for (const area of areas) {
      const colour = area.colour ?? CANVAS_COLOURS.fieldCells;
      g.fillStyle(colour.rgb, colour.alpha * (area.painting ? FIELD_CELLS_FILL.painting : FIELD_CELLS_FILL.shown));
      for (const [c, r] of area.cells) {
        g.fillRect(area.anchor.x + c * area.cellPixels, area.anchor.y - (r + 1) * area.cellPixels, area.cellPixels, area.cellPixels);
      }
      g.lineStyle(FIELD_CELLS_EDGE / zoom, colour.rgb, colour.alpha);
      for (const [x0, y0, x1, y1] of areaEdges(area)) {
        g.lineBetween(x0, y0, x1, y1);
      }
    }
    return true;
  }

  /**
   * Removes the drawing.
   */
  public destroy(): void {
    this.graphics.destroy();
  }
}
